/**
 * Post-processing for RTK VOID: bloom, tone mapping, then film grain.
 *
 * Plain three.js, no React (same rule as the rest of src/tsl), so a vanilla
 * Experience class can call createPostPipeline() unchanged if G1 drops R3F.
 *
 * Order matters:
 *   1. Scene pass in linear HDR (fog included), plus an 8-bit MRT bloom mask
 *      (src/tsl/bloomMask.ts): 1 by default, 0 on artworks.
 *   2. One bloom pass on sceneColor × mask with a luminance threshold: lights, wet-floor
 *      glints, frames and neon glow; artworks and the void don't.
 *   3. Tone mapping + sRGB, done by us (outputColorTransform = false) so that...
 *   4. FXAA in display space, only when MSAA is off (adaptive quality L3+).
 *   5. ...grain is added in display space, last, so FXAA never smears it. Grain before tone mapping gets crushed in
 *      the blacks and blown out in highlights; after, it reads evenly like film, and
 *      it doubles as dither for the black void (no banding in the fog falloff).
 */
import * as THREE from 'three/webgpu'
import {
  float,
  floor,
  luminance,
  mrt,
  output,
  pass,
  renderOutput,
  screenCoordinate,
  time,
  uint,
  uniform,
  vec3,
  vec4,
  hash,
  mix,
  clamp,
} from 'three/tsl'
import { bloom } from 'three/addons/tsl/display/BloomNode.js'
import { fxaa } from 'three/addons/tsl/display/FXAANode.js'
import { BLOOM_MASK, bloomMaskNode } from './bloomMask'

type Vec4Node = ReturnType<typeof vec4>
const floatUniform = (v: number) => uniform(v)
/** A float uniform node (mutable .value, no shader rebuild on change). */
type FloatUniform = ReturnType<typeof floatUniform>

/** masked: artworks excluded (default). all: everything bright blooms, for comparison. */
export type BloomSource = 'masked' | 'all'

export interface PostSettings {
  /** Bloom strength. 0 skips the bloom pass entirely (not just zero-weighted). */
  bloom: number
  bloomSource: BloomSource
  /** Bloom spread across mips, 0..1. */
  bloomRadius: number
  /** Luminance (linear HDR) above which pixels start to bloom. */
  bloomThreshold: number
  /** Grain amplitude in display space (0.05 = ±5% at mid grey). 0 removes the grain node. */
  grain: number
  /** Grain cell size in physical pixels. Tie it to dpr so phones and desktops read the same. */
  grainSize: number
  /** Grain refresh rate. Film is 24; per-frame noise at 60–120 Hz shimmers like video noise. */
  grainFps: number
  /** Scene-pass MSAA samples: 4, or 0 with FXAA instead (WebGPU allows only 1 or 4). Rebuild to change. */
  msaa: 0 | 4
  /** Bloom internal resolution vs the drawing buffer. Live (no rebuild); three's default is 0.5. */
  bloomResolution: number
}

export const defaultPostSettings: PostSettings = {
  bloom: 0.6,
  bloomSource: 'masked',
  bloomRadius: 0.2,
  bloomThreshold: 0.9,
  grain: 0.07,
  grainSize: 1,
  grainFps: 24,
  msaa: 4,
  bloomResolution: 0.5,
}

/**
 * Monochrome film grain in display space.
 * Integer PCG hash on (pixel cell, frame), so no sin() hash banding on mobile GPUs
 * and no precision loss as time grows. Two hashes give a triangular distribution,
 * which reads as grain rather than as uniform static.
 * Response: full in the mid-tones, ~25% in the deepest blacks (enough to dither the
 * void without lifting it), tapering in highlights.
 */
function filmGrain(input: Vec4Node, amount: FloatUniform, size: FloatUniform, fps: number): Vec4Node {
  const cell = floor(screenCoordinate.xy.div(size))
  // Frame index wraps every 4096 frames (~3 min at 24 fps); invisible, keeps uints exact.
  const frame = uint(floor(time.mul(fps)).mod(4096))
  const index = uint(cell.x).add(uint(cell.y).mul(uint(8191)))
  const seedA = index.bitXor(frame.mul(uint(2654435761)))
  const seedB = seedA.bitXor(uint(0x68e31da4))
  const n = hash(seedA).add(hash(seedB)).sub(1.0) // triangular, -1..1

  const l = clamp(luminance(input.rgb), 0.0, 1.0)
  const response = mix(float(0.25), float(1.0), l.mul(l.oneMinus()).mul(4.0).clamp(0.0, 1.0))
  const graded = input.rgb.add(vec3(n.mul(amount).mul(response)))
  return vec4(graded.max(0.0), input.a)
}

export interface PostPipeline {
  render(): void
  dispose(): void
  /** Live-tweakable without a shader rebuild. */
  uniforms: {
    bloomStrength: FloatUniform | null
    bloomRadius: FloatUniform | null
    bloomThreshold: FloatUniform | null
    grain: FloatUniform | null
    grainSize: FloatUniform | null
  }
  /** Bloom internal resolution, applied from the next frame. No-op without bloom. */
  setBloomResolution(scale: number): void
  describe(): string
}

export function createPostPipeline(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  settings: PostSettings = defaultPostSettings,
): PostPipeline {
  const pipeline = new THREE.RenderPipeline(renderer)
  // We tone map ourselves so grain lands after it.
  pipeline.outputColorTransform = false

  // samples: 0 means "no MSAA" to PassNode (undefined would inherit the renderer's 4).
  const scenePass = pass(scene, camera, { samples: settings.msaa })
  const useBloom = settings.bloom > 0
  const masked = useBloom && settings.bloomSource === 'masked'

  if (masked) {
    // Mask value per material: 1, or what setBloomMask() stored (src/tsl/bloomMask.ts).
    scenePass.setMRT(mrt({ output, [BLOOM_MASK]: bloomMaskNode() }))
    // A 0..1 mask needs no half-float: 8-bit keeps the extra bandwidth small on phones.
    scenePass.getTexture(BLOOM_MASK).type = THREE.UnsignedByteType
  }

  const sceneColor = scenePass.getTextureNode('output')
  let hdr = sceneColor as unknown as Vec4Node
  let bloomNode: ReturnType<typeof bloom> | null = null

  if (useBloom) {
    const source = masked ? sceneColor.mul(scenePass.getTextureNode(BLOOM_MASK).r) : sceneColor
    bloomNode = bloom(source, settings.bloom, settings.bloomRadius, settings.bloomThreshold)
    bloomNode.setResolutionScale(settings.bloomResolution)
    hdr = sceneColor.add(bloomNode) as unknown as Vec4Node
  }

  // Tone mapping and colour space taken from the renderer as configured by the Canvas (?tone=).
  let display = renderOutput(hdr, renderer.toneMapping, renderer.outputColorSpace) as unknown as Vec4Node

  // FXAA wants display-space (sRGB, tone-mapped) input; it adds one full-screen pass.
  const useFxaa = settings.msaa === 0
  if (useFxaa) display = fxaa(display) as unknown as Vec4Node

  let grainU: FloatUniform | null = null
  let grainSizeU: FloatUniform | null = null
  if (settings.grain > 0) {
    grainU = floatUniform(settings.grain)
    grainSizeU = floatUniform(Math.max(1, settings.grainSize))
    display = filmGrain(display, grainU, grainSizeU, settings.grainFps) as unknown as Vec4Node
  }

  pipeline.outputNode = display

  return {
    render: () => pipeline.render(),
    dispose: () => {
      bloomNode?.dispose()
      scenePass.dispose()
      pipeline.dispose()
    },
    uniforms: {
      bloomStrength: bloomNode ? (bloomNode.strength as FloatUniform) : null,
      bloomRadius: bloomNode ? (bloomNode.radius as FloatUniform) : null,
      bloomThreshold: bloomNode ? (bloomNode.threshold as FloatUniform) : null,
      grain: grainU,
      grainSize: grainSizeU,
    },
    setBloomResolution: (scale: number) => {
      if (bloomNode && bloomNode.getResolutionScale() !== scale) {
        bloomNode.setResolutionScale(scale)
        settings = { ...settings, bloomResolution: scale }
      }
    },
    describe: () =>
      [
        settings.msaa ? `msaa ${settings.msaa}x` : 'fxaa',
        useBloom ? `bloom ${settings.bloom} ${settings.bloomSource} @${settings.bloomResolution}` : 'bloom off',
        settings.grain > 0 ? `grain ${settings.grain}` : 'grain off',
      ].join(' · '),
  }
}
